/**
 * Help Center client (docs/kb.md): reads published content, runs the AI summary / chatbot streams
 * and (for platform admins) the management calls. Types come from @af/shared.
 */
import type {
  KbArticleDto,
  KbArticleSummary,
  KbCategoryDto,
  KbLocale,
  KbManageArticleDto,
  KbSearchHit,
  KbSource,
  KbStreamEvent,
} from '@af/shared';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { orgHeaders } from './active-org';
import { ApiClientError, rawCall } from './api';
import { createSSEParser } from './sse';

/** The Help Center language follows the app language (NL / EN). */
export function useKbLocale(): KbLocale {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith('nl') ? 'nl' : 'en';
}

const qs = (o: Record<string, string | number | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const kbKeys = {
  categories: (l: KbLocale) => ['kb', 'categories', l] as const,
  articles: (l: KbLocale, category?: string, type?: string) => ['kb', 'articles', l, category, type] as const,
  article: (slug: string, l: KbLocale) => ['kb', 'article', slug, l] as const,
  search: (q: string, l: KbLocale) => ['kb', 'search', q, l] as const,
  manage: ['kb', 'manage'] as const,
};

export function useKbCategories() {
  const locale = useKbLocale();
  return useQuery({
    queryKey: kbKeys.categories(locale),
    queryFn: () =>
      rawCall<{ items: KbCategoryDto[] }>('GET', `/api/kb/categories${qs({ locale })}`).then((r) => r.items),
    staleTime: 5 * 60_000,
  });
}

export function useKbArticles(o: { category?: string | undefined; type?: string | undefined } = {}) {
  const locale = useKbLocale();
  return useQuery({
    queryKey: kbKeys.articles(locale, o.category, o.type),
    queryFn: () =>
      rawCall<{ items: KbArticleSummary[] }>(
        'GET',
        `/api/kb/articles${qs({ locale, category: o.category, type: o.type })}`,
      ).then((r) => r.items),
    staleTime: 5 * 60_000,
  });
}

export function useKbArticle(slug: string) {
  const locale = useKbLocale();
  return useQuery({
    queryKey: kbKeys.article(slug, locale),
    queryFn: () =>
      rawCall<KbArticleDto>('GET', `/api/kb/articles/${encodeURIComponent(slug)}${qs({ locale })}`),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export interface KbSearchResult {
  items: KbSearchHit[];
  locale: KbLocale;
  aiAvailable: boolean;
}

/** Deterministic search ($0). `track` only for submitted searches (feeds the content-gap report). */
export function useKbSearch(q: string, o: { track: boolean }) {
  const locale = useKbLocale();
  const query = q.trim();
  return useQuery({
    queryKey: [...kbKeys.search(query, locale), o.track] as const,
    enabled: query.length >= 2,
    queryFn: () =>
      rawCall<KbSearchResult>('GET', `/api/kb/search${qs({ q: query, locale, track: o.track ? '1' : '0' })}`),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export const sendKbFeedback = (b: {
  articleId?: string;
  answerKey?: string;
  helpful: boolean;
  comment?: string;
}) => rawCall<void>('POST', '/api/kb/feedback', b);

/**
 * POST + Server-Sent Events (EventSource cannot POST). Resolves when the stream ends; every event is
 * passed to `onEvent`. Throws ApiClientError on a non-2xx answer.
 */
export async function streamKb(
  path: '/api/kb/answer' | '/api/kb/chat',
  body: unknown,
  onEvent: (e: KbStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { accept: 'text/event-stream', 'content-type': 'application/json', ...orgHeaders() },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok || !res.body) {
    const data = (await res.json().catch(() => null)) as {
      error?: { code?: string; message?: string };
    } | null;
    throw new ApiClientError(
      res.status,
      data?.error?.code ?? `http_${res.status}`,
      data?.error?.message ?? res.statusText,
    );
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let terminal = false;
  let hasText = false;
  let unavailable = false;
  const parser = createSSEParser((msg) => {
    let event: KbStreamEvent;
    try {
      event = JSON.parse(msg.data) as KbStreamEvent;
    } catch {
      return;
    }
    if (event.type === 'text' && event.text.trim()) hasText = true;
    if (event.type === 'unavailable' || event.type === 'incomplete') unavailable = true;
    if (event.type === 'done' || event.type === 'unavailable' || event.type === 'incomplete') terminal = true;
    // Rendering/state errors must reach the caller rather than being mistaken for bad JSON.
    onEvent(event);
  });
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.push(dec.decode(value, { stream: true }));
    }
    // A final event without its blank line is still delivered.
    parser.push(`${dec.decode()}\n\n`);
    if (!terminal) throw new Error('Help answer stream ended before completion');
    if (!hasText && !unavailable) throw new Error('Help answer was empty');
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

export interface KbAnswerState {
  status: 'idle' | 'streaming' | 'done' | 'unavailable' | 'error' | 'incomplete';
  text: string;
  sources: KbSource[];
  answerKey: string | null;
  reason?: 'no_key' | 'rate_limited' | 'budget' | 'error';
  cached?: boolean;
  costUsd?: number;
}

export const idleAnswer: KbAnswerState = { status: 'idle', text: '', sources: [], answerKey: null };

/** Fold one stream event into the state. */
export function reduceKbEvent(s: KbAnswerState, e: KbStreamEvent): KbAnswerState {
  switch (e.type) {
    case 'sources':
      return { ...s, sources: e.sources, answerKey: e.answerKey };
    case 'text':
      return { ...s, status: 'streaming', text: s.text + e.text };
    case 'done':
      return s.text.trim()
        ? { ...s, status: 'done', cached: e.cached, costUsd: e.costUsd }
        : { ...s, status: 'unavailable', reason: 'error' };
    case 'incomplete':
      return { ...s, status: 'incomplete', answerKey: null, cached: false, costUsd: e.costUsd };
    case 'unavailable':
      return { ...s, status: 'unavailable', reason: e.reason };
  }
}

export type { KbManageArticleDto };
